/**
 * useIcon Hook
 *
 * Primary hook for accessing icons with automatic theme and sizing integration.
 * This hook integrates with the ThemeContext and SizingContext to provide
 * icons that automatically respect the user's theme colors and sizing preferences.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { getIcon } = useIcon();
 *   const DashboardIcon = getIcon(IconName.DASHBOARD);
 *
 *   return <DashboardIcon size={24} />;
 * }
 * ```
 */

import { useIconContext } from './IconContext';
import { useSizing } from '@/styles/sizing';
import { IconName, IconSize } from './types';
import { getIconComponent, LucideIcon } from './registry';
import { sizing as designTokenSizing } from '@/styles/design-tokens';
import { logger } from '@/utils/logger';

/**
 * Options for retrieving an icon.
 */
interface GetIconOptions {
  /**
   * Size of the icon. Maps to design tokens.
   */
  size?: IconSize;
  /**
   * Custom color for the icon. If not provided, uses theme default.
   */
  color?: string;
}

/**
 * Return type for the useIcon hook.
 */
interface UseIconReturn {
  /**
   * Retrieves an icon component with optional size and color.
   */
  getIcon: (name: IconName, options?: GetIconOptions) => LucideIcon | null;

  /**
   * Gets the pixel size for an icon size token.
   */
  getIconSize: (size: IconSize) => number;
}

/**
 * Hook for accessing icons with automatic theme and sizing integration.
 *
 * Features:
 * - Automatic sizing based on design tokens and user preferences
 * - Automatic theme color integration
 * - Type-safe icon retrieval
 * - Developer-friendly error messages
 *
 * @returns Object with getIcon function and helper utilities
 *
 * @example
 * ```tsx
 * function StatCard() {
 *   const { getIcon, getIconSize } = useIcon();
 *   const Icon = getIcon(IconName.USERS, { size: 'lg' });
 *   const iconSize = getIconSize('lg');
 *
 *   return Icon ? <Icon size={iconSize} /> : null;
 * }
 * ```
 */
export function useIcon(): UseIconReturn {
  const { defaultSize } = useIconContext();
  const { multiplier } = useSizing();

  /**
   * Gets the calculated pixel size for an icon based on design tokens and sizing multiplier.
   */
  const getIconSize = (size: IconSize = defaultSize): number => {
    const baseSize = designTokenSizing.icon[size];

    // Defensive check: if baseSize is undefined, fall back to 'md' size
    if (!baseSize) {
      if (process.env.NODE_ENV === 'development') {
        logger.ui.warn(
          `Invalid icon size "${size}". Falling back to 'md' size.`
        );
      }
      const fallbackSize = designTokenSizing.icon.md;
      const match = fallbackSize?.match(/(\d+)px/);
      const basePx = match ? parseInt(match[1]) : 20;
      return Math.round(basePx * multiplier);
    }

    // Parse the base size (e.g., "calc(16px * var(--sizing-multiplier, 1))" -> 16)
    // Extract the pixel value from the calc() expression
    const match = baseSize.match(/(\d+)px/);
    const basePx = match ? parseInt(match[1]) : 20; // Default to 20 if parsing fails
    // Apply the sizing multiplier from context
    return Math.round(basePx * multiplier);
  };

  /**
   * Retrieves an icon component from the registry with optional configuration.
   */
  const getIcon = (
    name: IconName,
    _options?: GetIconOptions
  ): LucideIcon | null => {
    const IconComponent = getIconComponent(name);

    if (!IconComponent) {
      if (process.env.NODE_ENV === 'development') {
        logger.ui.warn(
          `Icon "${name}" not found in registry. ` +
            `Please add it to the iconRegistry in icons/registry.ts`
        );
      }
      return null;
    }

    return IconComponent;
  };

  return {
    getIcon,
    getIconSize,
  };
}
